import { ChangeEvent, useEffect } from 'react';

import usePermissions from 'api/usePermissions';
import { FormikProps } from 'common/contracts';
import { EIntervalsRepeatRequired, EWeekDay } from 'constants/enums';
import { permissionsUtil } from 'utils/permissionsUtil';
import { timeUtils } from 'utils/scheduleUtils';
import {
  dayInMonthOptions,
  intervalsRepeatOptionsRequired,
  roundedHoursOptions
} from 'utils/selectOptionsUtils';

import Input from 'components/Input/Input';
import InputSelectDropdown from 'components/InputSelectDropdown/InputSelectDropdown';

export interface IntervalOfferProps {
  formikProps: FormikProps;
  repeat: EIntervalsRepeatRequired;
  setRepeat: (repeat: EIntervalsRepeatRequired) => void;
  weekDay: number;
  setWeekDay: (day: number) => void;
  dayInMonth: number;
  setDayInMonth: (day: number) => void;
  publisherId?: string;
  intervalCron: string;
  setIntervalCron: (cron: string) => void;
}

const IntervalOffer: React.FC<IntervalOfferProps> = ({
  formikProps,
  repeat,
  setRepeat,
  weekDay,
  setWeekDay,
  dayInMonth,
  setDayInMonth,
  publisherId,
  intervalCron,
  setIntervalCron
}) => {
  const { values, setFieldValue, handleBlur, handleChange, touched, errors } =
    formikProps;
  const { isSuperAdminByProject } = usePermissions();

  useEffect(() => {
    if (!repeat) {
      setIntervalCron('');
      return;
    }

    const newCron = timeUtils.generateCronString(
      repeat,
      values.startHour,
      weekDay as number,
      dayInMonth as number
    );

    if (newCron !== intervalCron) {
      setIntervalCron(newCron || '');
    }
  }, [repeat, values.startHour, weekDay, dayInMonth]);

  useEffect(() => {
    if (values?.schedule?.permanent) {
      setFieldValue('schedule', {
        permanent: values?.schedule?.permanent,
        timeFrames: values?.schedule?.timeFrames,
        ...(repeat &&
          intervalCron &&
          values.schedule.permanent && { interval: intervalCron })
      });
    }
  }, [intervalCron]);

  useEffect(() => {
    const interval = values?.schedule?.interval;

    if (!interval) {
      return;
    }

    const { dayInMonthFromCron, hourFromCron, weekDayFromCron } =
      timeUtils.getTimeFromCron(interval);

    const newRepeat = dayInMonthFromCron
      ? EIntervalsRepeatRequired.MONTHLY
      : weekDayFromCron !== undefined
        ? EIntervalsRepeatRequired.WEEKLY
        : EIntervalsRepeatRequired.DAILY;

    if (newRepeat !== repeat) setRepeat(newRepeat);
    if (dayInMonthFromCron && dayInMonthFromCron !== dayInMonth)
      setDayInMonth(dayInMonthFromCron);
    if (weekDayFromCron !== undefined && weekDayFromCron !== weekDay)
      setWeekDay(weekDayFromCron);
  }, [values?.schedule?.interval]);

  const weekDaysOptions = Object.entries(EWeekDay)
    .filter(([key]) => isNaN(Number(key)))
    .map(([key, value]) => ({
      label: key.charAt(0).toUpperCase() + key.slice(1).toLowerCase(),
      value
    }));

  const isSuperAdmin =
    publisherId && isSuperAdminByProject(publisherId);

  return (
    <div className="mt-8">
      <div className="flex flex gap-5 mb-6">
        <Input
          label="Availability"
          name="playerAvailability"
          placeholder="Set availability"
          value={values.playerAvailability || ''}
          onChange={handleChange}
          onBlur={handleBlur}
          isRequired={values?.setAsFree || values?.schedule?.interval}
          type="number"
          state={
            touched.playerAvailability && errors.playerAvailability
              ? 'error'
              : 'default'
          }
          description={
            touched.playerAvailability && errors.playerAvailability
              ? errors.playerAvailability
              : ''
          }
          onKeyDown={(e) => {
            if (e.key === '-' || e.key === 'Minus') {
              e.preventDefault();
            }
          }}
          min={0}
        />
        {/* Repeat */}
        <InputSelectDropdown
          label="Repeat"
          listDataTestId="intervalRepeatList"
          buttonDataTestId="intervalRepeatButton"
          options={intervalsRepeatOptionsRequired}
          defaultSelected={intervalsRepeatOptionsRequired.find(
            (option) => option.value === repeat
          )}
          onOptionChange={(value: string) =>
            setRepeat(value as EIntervalsRepeatRequired)
          }
        />
      </div>
      <div className="flex flex gap-5">
        {/* Week Day */}
        {repeat === EIntervalsRepeatRequired.WEEKLY && (
          <InputSelectDropdown
            label="Week Day"
            options={weekDaysOptions}
            defaultSelected={weekDaysOptions.find(
              (option) => option.value === weekDay
            )}
          />
        )}
        {/* Day in Month */}
        {repeat === EIntervalsRepeatRequired.MONTHLY && (
          <InputSelectDropdown
            label="Day in Month"
            options={dayInMonthOptions}
            defaultSelected={dayInMonthOptions.find(
              (option) => option.value === dayInMonth
            )}
          />
        )}
        <div className="flex flex-col min-w-[220px]">
          {/* Hour (UTC) */}
          {isSuperAdmin ? (
            <Input
              label="Hour (UTC)"
              type="time"
              name="interval"
              value={values.startHour}
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                setFieldValue('startHour', e.target.value);
              }}
              onBlur={handleBlur}
              isRequired
              state={
                touched?.interval && errors?.interval
                  ? 'error'
                  : 'default'
              }
              description={
                touched?.interval && errors?.interval
                  ? errors?.interval
                  : ''
              }
            />
          ) : (
            <InputSelectDropdown
              label="Hour (UTC)"
              options={roundedHoursOptions}
              defaultSelected={roundedHoursOptions.find(
                (option) => option.value === values.startHour
              )}
              onOptionChange={(value: string) =>
                setFieldValue('startHour', value as string)
              }
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default IntervalOffer;
